import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { useLenis } from "lenis/react";
import { MOBILE_MAX } from "@/providers/ViewSizeContext";
import { getReduceMotion, REDUCE_MOTION_CHANNEL_NAME } from "@/utils/reduceMotion";
import { useParallax } from "@/components/Parallax/useParallax";

gsap.registerPlugin(ScrollTrigger);

const LANDING_DIM_OPACITY = 0.75;

type QuizMachine = {
  started: boolean;
  phase: "quiz" | "results";
  current: number;
  begin: () => void;
  retake: () => void;
  goNext: () => void;
  goBack: () => void;
};

export function useQuizIntro(machine: QuizMachine, dimImagery = false) {
  const { started, phase, current } = machine;

  const lenis = useLenis();
  const rootRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const lockupRef = useRef<HTMLDivElement>(null);

  const introTlRef = useRef<gsap.core.Timeline | null>(null);
  const introDoneRef = useRef(false);
  // Guards re-entrant begin/back/retake while a wipe is mid-flight.
  const transitioningRef = useRef(false);
  // Guards re-entrant NEXT/BACK while a question is fading out.
  const questionTransitionRef = useRef(false);
  // The onSettled callback a reverse-in-progress owes; flushed if the timeline
  // is killed (e.g. revertOnUpdate) before onReverseComplete can fire it.
  const pendingSettleRef = useRef<(() => void) | null>(null);

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    setReduceMotion(getReduceMotion());
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(REDUCE_MOTION_CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (typeof event.data?.checked === "boolean") setReduceMotion(event.data.checked);
    };
    return () => channel.close();
  }, []);

  useParallax({ containerRef: rootRef, startPx: -45, endPx: 45, isEnabled: !reduceMotion });

  const revealInset = () => {
    const panelW = panelRef.current?.offsetWidth ?? 0;
    const stage = stageRef.current;
    const gap = stage ? parseFloat(getComputedStyle(stage).columnGap) : NaN;
    return panelW + (Number.isFinite(gap) ? gap : 24);
  };

  useGSAP(
    () => {
      if (transitioningRef.current) {
        const pendingSettle = pendingSettleRef.current;
        pendingSettleRef.current = null;
        transitioningRef.current = false;
        pendingSettle?.();
      }

      if (panelRef.current) panelRef.current.inert = !started;
      if (lockupRef.current) lockupRef.current.inert = started;

      const media = mediaRef.current;
      const img = imgRef.current;
      const lockup = lockupRef.current;
      const root = rootRef.current;
      const dim = media?.querySelector<HTMLElement>("[data-quiz-dim]") ?? null;

      if (!started) {
        if (media) {
          media.style.right = "";
          media.style.height = "";
        }
        if (img) gsap.set(img, { clearProps: "transform" });
        if (lockup) gsap.set(lockup, { clearProps: "opacity" });
        if (dim) gsap.set(dim, { clearProps: "opacity" });
        if (root) gsap.set(root.querySelectorAll("[data-quiz-stagger]"), { clearProps: "opacity,transform" });
        return;
      }
      if (!root || !media) return;

      const isMobile = window.innerWidth <= MOBILE_MAX;
      const targets = root.querySelectorAll<HTMLElement>("[data-quiz-stagger]");

      if (reduceMotion) {
        media.style.right = "";
        media.style.height = "";
        gsap.set(targets, { opacity: 1, y: 0 });
        if (lockup) gsap.set(lockup, { opacity: 0 });
        if (dim) gsap.set(dim, { clearProps: "opacity" });
        introDoneRef.current = true;
        return;
      }

      gsap.set(targets, { opacity: 0, y: 20 });

      const tl = gsap.timeline({
        onStart: () => {
          transitioningRef.current = true;
        },
        onComplete: () => {
          introDoneRef.current = true;
          transitioningRef.current = false;
          media.style.right = "";
          media.style.height = "";
          if (dim) dim.style.opacity = "";
        },
      });

      if (!isMobile) {
        const inset = revealInset();
        tl.fromTo(media, { right: 0 }, { right: inset, duration: 1.2, ease: "expo.inOut" }, 0);
      } else {
        const stageH = stageRef.current?.offsetHeight ?? window.innerHeight;
        const bannerH = media.offsetHeight;
        tl.fromTo(media, { height: stageH }, { height: bannerH, duration: 1.2, ease: "expo.inOut" }, 0);
      }
      if (lockup) tl.fromTo(lockup, { opacity: 1 }, { opacity: 0, duration: 0.5, ease: "power2.in" }, 0);
      
      if (dim) {
        tl.fromTo(
          dim,
          { opacity: dimImagery ? LANDING_DIM_OPACITY : 0 },
          { opacity: 0, duration: 0.4, ease: "power1.inOut" },
          0.3
        );
      }
      tl.to(targets, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.08 }, 0.4);
      introTlRef.current = tl;
    },
    { dependencies: [started, reduceMotion], revertOnUpdate: true, scope: rootRef }
  );

  // ---- per-question re-stagger (after the intro settles) ----
  useGSAP(
    () => {
      if (!started || phase !== "quiz" || !introDoneRef.current || reduceMotion) return;
      const el = rootRef.current?.querySelector<HTMLElement>("[data-quiz-question]");
      if (el) gsap.fromTo(el, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" });
    },
    { dependencies: [current], scope: rootRef }
  );

  // ---- results intro re-stagger ----
  useGSAP(
    () => {
      if (phase !== "results" || reduceMotion) return;
      const targets = rootRef.current?.querySelectorAll<HTMLElement>("[data-quiz-stagger]");
      if (targets?.length) {
        gsap.fromTo(targets, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.08 });
      }
    },
    { dependencies: [phase], scope: rootRef }
  );

  // Square up the module in the viewport, then run the state transition.
  const squareUp = () => {
    if (rootRef.current) lenis?.scrollTo(rootRef.current, { offset: 0, duration: 0.6 });
  };

  const handleBegin = () => {
    if (transitioningRef.current || started) return;
    squareUp();
    machine.begin();
  };

  const reverseToLanding = (onSettled: () => void) => {
    squareUp();
    const tl = introTlRef.current;
    if (reduceMotion || !tl) {
      onSettled();
      return;
    }
    transitioningRef.current = true;
    pendingSettleRef.current = onSettled;
    introDoneRef.current = false;
    tl.eventCallback("onReverseComplete", () => {
      tl.eventCallback("onReverseComplete", null);
      transitioningRef.current = false;
      pendingSettleRef.current = null;
      onSettled();
    });
    tl.timeScale(1.7); // close snappier than it opened
    tl.reverse();
  };

  const advanceQuestion = (change: () => void) => {
    if (questionTransitionRef.current) return;
    const el = rootRef.current?.querySelector<HTMLElement>("[data-quiz-question]");
    if (reduceMotion || !el) {
      change();
      return;
    }
    questionTransitionRef.current = true;
    gsap.to(el, {
      opacity: 0,
      duration: 0.15,
      ease: "power2.in",
      onComplete: () => {
        questionTransitionRef.current = false;
        change();
      },
    });
  };

  const handleNext = () => {
    if (transitioningRef.current) return;
    advanceQuestion(machine.goNext);
  };

  const handleBack = () => {
    if (transitioningRef.current) return;
    if (started && phase === "quiz" && current === 0) {
      reverseToLanding(() => machine.goBack());
      return;
    }
    advanceQuestion(machine.goBack);
  };

  const handleRetake = () => {
    if (transitioningRef.current) return;
    reverseToLanding(() => machine.retake());
  };

  return {
    rootRef,
    stageRef,
    mediaRef,
    imgRef,
    panelRef,
    lockupRef,
    reduceMotion,
    handleBegin,
    handleNext,
    handleBack,
    handleRetake,
  };
}
